import type { CommissionId } from '@sorrem/shared';
import { useCallback } from 'react';
import { useAcceptCommissionMutation } from '@sorrem-client/ui/store';
import { useCurrentCharacterId } from './use-current-character-id';

export interface AcceptCommissionState {
    isAccepting: boolean;
    /**
     * Accepts a commission, or re-enters one already accepted: either way the
     * server provisions a fresh Crucible for it. Resolves to the new
     * Crucible's id, or null if it failed.
     */
    accept: (commissionId: CommissionId) => Promise<string | null>;
}

/** Accepting (or returning to) the selected character's commissions. */
export function useAcceptCommission(): AcceptCommissionState {
    const { characterId } = useCurrentCharacterId();
    const [acceptCommission, { isLoading: isAccepting }] = useAcceptCommissionMutation();

    const accept = useCallback(async (commissionId: CommissionId): Promise<string | null> => {
        if (characterId === null) {
            return null;
        }
        try {
            const crucible = await acceptCommission({ commissionId, characterId }).unwrap();
            return crucible.id;
        } catch {
            return null;
        }
    }, [acceptCommission, characterId]);

    return { isAccepting, accept };
}
